/**
 * Smart Variant Grid
 * Handles multi-SKU quantity selection, subtotal calculation,
 * and batch add-to-cart functionality.
 */
(function () {
  'use strict';

  const SELECTORS = {
    section: '.smart-main-product',
    grid: '[data-smart-variant-grid]',
    item: '.smart-variant-grid__item',
    qtyValue: '[data-qty-value]',
    qtyBtnMinus: '[data-action="decrease"]',
    qtyBtnPlus: '[data-action="increase"]',
    subtotalPrice: '[data-smart-subtotal]',
    itemCount: '[data-smart-item-count]',
    addToCart: '[data-smart-add-to-cart]',
    productJson: '[data-smart-product-json]',
  };

  class SmartVariantGrid {
    constructor(section) {
      this.section = section;
      if (!this.section) return;

      this.grid = this.section.querySelector(SELECTORS.grid);
      if (!this.grid) return;

      this.productData = this.getProductData();
      this.selections = {}; // { variantId: quantity }

      this.bindEvents();
      this.syncButtonStates();
    }

    getProductData() {
      const jsonEl = this.section.querySelector(SELECTORS.productJson);
      if (!jsonEl) return null;
      try {
        return JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('Smart Variant Grid: Failed to parse product JSON', e);
        return null;
      }
    }

    bindEvents() {
      // Quantity buttons
      this.grid.addEventListener('click', (e) => {
        const btn = e.target.closest('.smart-variant-grid__qty-btn');
        if (!btn) return;

        const item = btn.closest(SELECTORS.item);
        if (!item) return;

        const variantId = item.dataset.variantId;
        const action = btn.dataset.action;

        this.updateQuantity(variantId, action, item);
      });

      // Add to Cart button
      const addToCartBtn = this.section.querySelector(SELECTORS.addToCart);
      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', () => this.addToCart());
      }
    }

    updateQuantity(variantId, action, itemEl) {
      const currentQty = this.selections[variantId] || 0;
      let newQty = currentQty;

      if (action === 'increase') {
        newQty = currentQty + 1;
      } else if (action === 'decrease') {
        newQty = Math.max(0, currentQty - 1);
      }

      this.selections[variantId] = newQty;

      // Update UI
      const qtyDisplay = itemEl.querySelector(SELECTORS.qtyValue);
      if (qtyDisplay) {
        qtyDisplay.textContent = newQty;
      }

      // Toggle selected state
      if (newQty > 0) {
        itemEl.classList.add('smart-variant-grid__item--selected');
      } else {
        itemEl.classList.remove('smart-variant-grid__item--selected');
      }

      this.updateSubtotal();
      this.syncButtonStates();
      this.dispatchUpdateEvent();
    }

    syncButtonStates() {
      this.grid.querySelectorAll(SELECTORS.item).forEach((item) => {
        const variantId = item.dataset.variantId;
        const qty = this.selections[variantId] || 0;
        const isAvailable = item.dataset.variantAvailable === 'true';
        const plus = item.querySelector(SELECTORS.qtyBtnPlus);
        const minus = item.querySelector(SELECTORS.qtyBtnMinus);

        if (plus) plus.disabled = !isAvailable;
        if (minus) minus.disabled = !isAvailable || qty === 0;
      });
    }

    updateSubtotal() {
      let totalItems = 0;
      let totalPrice = 0;

      Object.entries(this.selections).forEach(([variantId, qty]) => {
        if (qty > 0) {
          totalItems += qty;
          const variant = this.productData?.variants?.find(
            (v) => String(v.id) === String(variantId)
          );
          if (variant) {
            totalPrice += variant.price * qty;
          }
        }
      });

      // Update subtotal display
      const subtotalEl = this.section.querySelector(SELECTORS.subtotalPrice);
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(totalPrice);
      }

      const itemCountEl = this.section.querySelector(SELECTORS.itemCount);
      if (itemCountEl) {
        itemCountEl.textContent = totalItems;
      }
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    getSelectedItems() {
      const items = [];
      Object.entries(this.selections).forEach(([variantId, qty]) => {
        if (qty > 0) {
          const variant = this.productData?.variants?.find(
            (v) => String(v.id) === String(variantId)
          );
          items.push({
            id: String(variantId),
            quantity: qty,
            title: variant ? variant.title : '',
            price: variant ? variant.price : 0,
          });
        }
      });
      return items;
    }

    dispatchUpdateEvent() {
      const items = this.getSelectedItems();
      let totalPrice = 0;
      let totalItems = 0;

      items.forEach((item) => {
        totalPrice += item.price * item.quantity;
        totalItems += item.quantity;
      });

      window.dispatchEvent(
        new CustomEvent('smart:variant-grid:update', {
          detail: {
            items,
            totalPrice,
            totalItems,
            formattedTotal: this.formatMoney(totalPrice),
          },
        })
      );
    }

    async addToCart() {
      const items = this.getSelectedItems();
      if (items.length === 0) return;

      const addToCartBtn = this.section.querySelector(SELECTORS.addToCart);
      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('loading');
      }

      const cart = this.getCartTarget(items);
      this.prepareCartTarget(cart);

      try {
        const data = await this.addItemsToCart(items, cart);
        this.onAddToCartSuccess(data, cart);
      } catch (error) {
        console.error('Smart Variant Grid: Add to cart failed', error);
        this.onAddToCartError(error?.cartError || { message: 'Network error. Please try again.' });
      } finally {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
          addToCartBtn.classList.remove('loading');
        }
      }
    }

    prepareCartTarget(cart) {
      if (cart && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    async addItemsToCart(items, cart) {
      let latestData = null;

      // Submit one variant at a time to avoid Shopify's generic batch
      // "Cart Error" response hiding which line item caused the issue.
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const isLastItem = index === items.length - 1;
        const payload = this.getCartAddFormData(item);

        if (isLastItem) {
          this.appendCartSections(payload, cart);
        }

        latestData = await this.addSingleItemToCart(payload, item);
      }

      return latestData;
    }

    async addSingleItemToCart(payload, item) {
      const response = await fetch(this.getCartAddUrl(), {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: payload,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        const label = item?.title ? `${item.title}: ` : '';
        data.description = `${label}${data.description || data.errors || data.message || 'Could not add to cart.'}`;
        const error = new Error(data.description);
        error.cartError = data;
        throw error;
      }

      return data;
    }

    getCartAddFormData(item) {
      const formData = new FormData();
      formData.append('id', String(item.id));
      formData.append('quantity', String(item.quantity));
      return formData;
    }

    appendCartSections(formData, cart) {
      if (!cart || typeof cart.getSectionsToRender !== 'function') return;

      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
    }

    getCartAddUrl() {
      const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
      return cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`;
    }

    getCartTarget(items) {
      // Prefer the drawer for batch adds; otherwise use the theme notification
      // and provide it with the first added item key after Shopify responds.
      return (
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification')
      );
    }

    onAddToCartSuccess(data, cart) {
      // Dispatch event for cart drawer/notification
      window.dispatchEvent(
        new CustomEvent('smart:cart:added', { detail: data })
      );

      if (cart && data.sections && typeof cart.renderContents === 'function') {
        if (cart.tagName.toLowerCase() === 'cart-notification' && !data.key) {
          data.key = data.items?.[0]?.key;
        }
        cart.renderContents(data);
        if (cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      } else if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
      }

      // Reset quantities
      this.resetSelections();
    }

    onAddToCartError(data) {
      console.error('Smart Variant Grid: Cart add error', data);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'smart-variant-grid',
          errors: data.errors || data.description,
          message: data.message,
        });
      }
      alert(data.description || data.errors || data.message || 'Could not add to cart.');
    }

    resetSelections() {
      this.selections = {};
      const items = this.grid.querySelectorAll(SELECTORS.item);
      items.forEach((item) => {
        item.classList.remove('smart-variant-grid__item--selected');
        const qtyDisplay = item.querySelector(SELECTORS.qtyValue);
        if (qtyDisplay) qtyDisplay.textContent = '0';
      });
      this.updateSubtotal();
      this.syncButtonStates();
      this.dispatchUpdateEvent();
    }
  }

  // Initialize when DOM is ready
  const initSmartVariantGrids = () => {
    document
      .querySelectorAll(SELECTORS.section)
      .forEach((section) => new SmartVariantGrid(section));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmartVariantGrids);
  } else {
    initSmartVariantGrids();
  }
})();
